/**
 * Vigila las condiciones experimentales mientras la captura está activa. Ante cualquier cambio
 * llama `onInterrupt` una sola vez (hasta `stop()`): pantalla completa perdida, pestaña oculta,
 * ventana sin foco, cambio de tamaño/zoom o de densidad de píxeles.
 */
import type { InterruptionReason } from "@gazelab/shared";

export interface ViewportSnapshot {
  width: number;
  height: number;
  devicePixelRatio: number;
}

export function snapshotViewport(win: Pick<Window, "innerWidth" | "innerHeight" | "devicePixelRatio"> = window): ViewportSnapshot {
  return { width: win.innerWidth, height: win.innerHeight, devicePixelRatio: win.devicePixelRatio };
}

/** Compara con la línea base; tolera ±1 px por redondeos del navegador. */
export function viewportChange(baseline: ViewportSnapshot, current: ViewportSnapshot): InterruptionReason | null {
  if (baseline.devicePixelRatio !== current.devicePixelRatio) return "device_pixel_ratio_change";
  if (Math.abs(baseline.width - current.width) > 1 || Math.abs(baseline.height - current.height) > 1) return "viewport_resize";
  return null;
}

export interface MonitorOptions {
  onInterrupt: (reason: InterruptionReason) => void;
  /** Exigir pantalla completa (desactivado solo en modo de prueba etiquetado sin fullscreen). */
  requireFullscreen: boolean;
}

export function startInterruptionMonitor(opts: MonitorOptions): () => void {
  const baseline = snapshotViewport();
  let fired = false;
  const fire = (reason: InterruptionReason) => {
    if (fired) return;
    fired = true;
    opts.onInterrupt(reason);
  };

  const onFullscreen = () => {
    if (opts.requireFullscreen && !document.fullscreenElement) fire("fullscreen_exit");
  };
  const onVisibility = () => {
    if (document.visibilityState === "hidden") fire("visibility_hidden");
  };
  const onBlur = () => fire("window_blur");
  const onResize = () => {
    const reason = viewportChange(baseline, snapshotViewport());
    if (reason) fire(reason);
  };
  const mq = window.matchMedia?.(`(resolution: ${baseline.devicePixelRatio}dppx)`);
  const onDpr = () => fire("device_pixel_ratio_change");

  document.addEventListener("fullscreenchange", onFullscreen);
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("blur", onBlur);
  window.addEventListener("resize", onResize);
  mq?.addEventListener("change", onDpr);

  // Estado inicial: si ya no se cumple alguna condición, interrumpir de inmediato.
  onFullscreen();
  onVisibility();

  return () => {
    fired = true;
    document.removeEventListener("fullscreenchange", onFullscreen);
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("blur", onBlur);
    window.removeEventListener("resize", onResize);
    mq?.removeEventListener("change", onDpr);
  };
}
